import { useState, useCallback, useRef, useEffect } from 'react';

interface DateGroup<T> {
  dateKey: string;
  events: T[];
}

interface UseInfiniteCalendarScrollOptions {
  pageSize?: number;
  initialLoadAroundToday?: boolean;
}

interface UseInfiniteCalendarScrollResult<T> {
  visibleGroups: DateGroup<T>[];
  hasMoreUp: boolean;
  hasMoreDown: boolean;
  loadMoreUp: () => void;
  loadMoreDown: () => void;
  isLoadingUp: boolean;
  isLoadingDown: boolean;
  todayGroupIndex: number;
  setScrollContainer: (container: HTMLElement | null) => void;
}

/**
 * Hook pour gérer le chargement progressif bidirectionnel d'un calendrier en liste.
 * Charge les éléments par page (default 20) en scrollant vers le haut ou vers le bas.
 */
export function useInfiniteCalendarScroll<T extends { date: Date }>(
  allGroups: DateGroup<T>[],
  options: UseInfiniteCalendarScrollOptions = {}
): UseInfiniteCalendarScrollResult<T> {
  const { pageSize = 20, initialLoadAroundToday = true } = options;
  
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [isLoadingUp, setIsLoadingUp] = useState(false);
  const [isLoadingDown, setIsLoadingDown] = useState(false);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  const previousScrollHeightRef = useRef<number>(0);
  const isLoadingUpRef = useRef(false);
  
  // Find today's index once
  const todayGroupIndex = useCallback(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Find first group where date is >= today
    for (let i = 0; i < allGroups.length; i++) {
      const groupDate = new Date(allGroups[i].events[0].date);
      groupDate.setHours(0, 0, 0, 0);
      if (groupDate >= today) {
        return i;
      }
    }
    // If all dates are in the past, return the last one
    return Math.max(0, allGroups.length - 1);
  }, [allGroups]);
  
  // Initialize indices around today
  useEffect(() => {
    if (allGroups.length === 0) {
      setStartIndex(0);
      setEndIndex(0);
      return;
    }
    
    if (initialLoadAroundToday) {
      const todayIdx = todayGroupIndex();
      // Load pageSize/2 before today and pageSize/2 after
      const halfPage = Math.floor(pageSize / 2);
      const newStart = Math.max(0, todayIdx - halfPage);
      const newEnd = Math.min(allGroups.length, newStart + pageSize);
      setStartIndex(newStart);
      setEndIndex(newEnd);
    } else {
      setStartIndex(0);
      setEndIndex(Math.min(pageSize, allGroups.length));
    }
  }, [allGroups.length, initialLoadAroundToday, pageSize, todayGroupIndex]);
  
  const hasMoreUp = startIndex > 0;
  const hasMoreDown = endIndex < allGroups.length;
  
  const visibleGroups = allGroups.slice(startIndex, endIndex);
  
  const loadMoreUp = useCallback(() => {
    if (isLoadingUpRef.current || startIndex <= 0) return;
    
    isLoadingUpRef.current = true;
    setIsLoadingUp(true);
    
    // Store current scroll height before adding items
    if (scrollContainerRef.current) {
      previousScrollHeightRef.current = scrollContainerRef.current.scrollHeight;
    }
    
    // Small delay to simulate loading and allow UI feedback
    requestAnimationFrame(() => {
      const newStart = Math.max(0, startIndex - pageSize);
      setStartIndex(newStart);
      setIsLoadingUp(false);
      isLoadingUpRef.current = false;
    });
  }, [startIndex, pageSize]);
  
  const loadMoreDown = useCallback(() => {
    if (isLoadingDown || endIndex >= allGroups.length) return;
    
    setIsLoadingDown(true);
    
    requestAnimationFrame(() => {
      const newEnd = Math.min(allGroups.length, endIndex + pageSize);
      setEndIndex(newEnd);
      setIsLoadingDown(false);
    });
  }, [endIndex, allGroups.length, pageSize, isLoadingDown]);
  
  // Maintain scroll position when loading items at the top
  useEffect(() => {
    if (scrollContainerRef.current && previousScrollHeightRef.current > 0) {
      const newScrollHeight = scrollContainerRef.current.scrollHeight;
      const scrollDiff = newScrollHeight - previousScrollHeightRef.current;
      if (scrollDiff > 0) {
        scrollContainerRef.current.scrollTop += scrollDiff;
      }
      previousScrollHeightRef.current = 0;
    }
  }, [startIndex]);
  
  const setScrollContainer = useCallback((container: HTMLElement | null) => {
    scrollContainerRef.current = container;
  }, []);
  
  // Get the visible index of today relative to visible groups
  const visibleTodayIndex = todayGroupIndex() - startIndex;
  
  return {
    visibleGroups,
    hasMoreUp,
    hasMoreDown,
    loadMoreUp,
    loadMoreDown,
    isLoadingUp,
    isLoadingDown,
    todayGroupIndex: Math.max(visibleTodayIndex, 0),
    setScrollContainer,
  };
}
